export type EmoteIconKey =
  | 'victory'
  | 'defeat'
  | 'salute'
  | 'laugh'
  | 'glare'
  | 'crown'
  | 'bow'
  | 'rage'
  | 'smirk';

export type EmoteDef = {
  id: string;
  label: string;
  iconKey: EmoteIconKey;
  rarity?: 'common' | 'uncommon' | 'rare';
  free?: boolean;
  requiresSkinId?: string;
};

export const EMOTES: EmoteDef[] = [
  { id: 'emote-victory', label: 'Victory', iconKey: 'victory', free: true, rarity: 'common' },
  { id: 'emote-defeat', label: 'Defeat', iconKey: 'defeat', free: true, rarity: 'common' },
  { id: 'emote-salute', label: 'Salute', iconKey: 'salute', free: true, rarity: 'common' },
  { id: 'emote-laugh', label: 'Laugh', iconKey: 'laugh', free: true, rarity: 'common' },
  { id: 'emote-glare', label: 'Glare', iconKey: 'glare', free: true, rarity: 'common' },
  { id: 'emote-crown', label: 'Crown', iconKey: 'crown', free: true, rarity: 'common' },
  {
    id: 'emote-realm-bow',
    label: 'Realm Bow',
    iconKey: 'bow',
    requiresSkinId: 'skin-realm',
    rarity: 'rare',
  },
  {
    id: 'emote-crimson-rage',
    label: 'Crimson Rage',
    iconKey: 'rage',
    requiresSkinId: 'color-crimson',
    rarity: 'uncommon',
  },
  {
    id: 'emote-azure-smirk',
    label: 'Azure Smirk',
    iconKey: 'smirk',
    requiresSkinId: 'color-azure',
    rarity: 'uncommon',
  },
  {
    id: 'emote-store-wave',
    label: 'Battlefield Wave',
    iconKey: 'salute',
    rarity: 'uncommon',
  },
  {
    id: 'emote-store-roar',
    label: 'War Roar',
    iconKey: 'rage',
    rarity: 'rare',
  },
  {
    id: 'emote-s1-taunt',
    label: 'S1 Taunt',
    iconKey: 'glare',
    rarity: 'rare',
  },
  {
    id: 'emote-s1-cheer',
    label: 'S1 Cheer',
    iconKey: 'victory',
    rarity: 'rare',
  },
  {
    id: 'emote-s1-flex',
    label: 'S1 Flex',
    iconKey: 'crown',
    rarity: 'rare',
  },
];

export const DEFAULT_EMOTE_ID = 'emote-crown';

export const DEFAULT_EMOTE_LOADOUT = [
  'emote-victory',
  'emote-salute',
  'emote-laugh',
  'emote-crown',
  'emote-glare',
] as const;

export const EMOTE_LOADOUT_SIZE = 5;

const byId = new Map(EMOTES.map((e) => [e.id, e]));

export function getEmoteDef(emoteId?: string | null): EmoteDef | undefined {
  if (!emoteId) return undefined;
  return byId.get(emoteId);
}

function ownedSkinSet(ownedColorIds?: unknown): Set<string> {
  const list = Array.isArray(ownedColorIds) ? ownedColorIds.map(String) : [];
  return new Set(['color-slate', 'color-crimson', ...list]);
}

export function canUseEmote(
  emoteId: string,
  ownedColorIds?: unknown,
  ownedEmoteIds?: unknown
): boolean {
  const def = byId.get(emoteId);
  if (!def) return false;
  if (def.free) return true;
  const ownedEmotes = Array.isArray(ownedEmoteIds)
    ? ownedEmoteIds.map(String)
    : [];
  if (ownedEmotes.includes(emoteId)) return true;
  if (def.requiresSkinId && ownedSkinSet(ownedColorIds).has(def.requiresSkinId)) {
    return true;
  }
  return false;
}

export function emoteUnlockHint(def: EmoteDef): string {
  if (def.free) return 'Free';
  if (def.id.startsWith('emote-s1-')) return 'Season 1 Battle Pass';
  if (def.id.startsWith('emote-store-')) return 'Store';
  if (def.requiresSkinId === 'skin-realm') return 'Own Realm skin';
  if (def.requiresSkinId) return `Own ${def.requiresSkinId.replace(/^color-/, '')} skin`;
  return 'Locked';
}

export function resolveEmoteLoadout(
  loadout: unknown,
  ownedColorIds?: unknown,
  ownedEmoteIds?: unknown
): string[] {
  const raw = Array.isArray(loadout) ? loadout.map(String) : [];
  const valid = raw.filter((id) =>
    canUseEmote(id, ownedColorIds, ownedEmoteIds)
  );
  const out: string[] = [];
  for (const id of valid) {
    if (out.length >= EMOTE_LOADOUT_SIZE) break;
    if (!out.includes(id)) out.push(id);
  }
  for (const id of DEFAULT_EMOTE_LOADOUT) {
    if (out.length >= EMOTE_LOADOUT_SIZE) break;
    if (!out.includes(id)) out.push(id);
  }
  return out;
}
